/* ════════════════════════════════════════════════════════════════════════
   LIFESTYLE BLOB BADGE  ·  assets/lifestyle-blob.js
   ────────────────────────────────────────────────────────────────────────
   Custom element <lifestyle-blob>. Clicking the corner badge flies a card
   from the badge's position to a centered lightbox (FLIP) while the corner
   "blob" radius relaxes into a rounded rectangle, then fades in the caption.
   Closing reverses it. Esc / backdrop / ✕ all close. Reduced motion honored.

   No dependencies. Self-registers; safe to load deferred.
   ════════════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  const REDUCE = matchMedia('(prefers-reduced-motion: reduce)').matches;

  class LifestyleBlob extends HTMLElement {
    connectedCallback() {
      this.badge    = this.querySelector('.lifestyle-blob__badge');
      this.backdrop = this.querySelector('[data-ls-backdrop]');
      this.card     = this.querySelector('[data-ls-card]');
      this.closeBtn = this.querySelector('[data-ls-close]');
      if (!this.badge || !this.card || !this.backdrop) return;

      this.open = false;
      this._onKey = (e) => { if (e.key === 'Escape') this.close(); };
      this._onResize = () => { if (this.open) this._sizeCard(this._targetRect()); };

      this.badge.addEventListener('click', () => this.expand());
      this.backdrop.addEventListener('click', () => this.close());
      this.closeBtn && this.closeBtn.addEventListener('click', () => this.close());
      addEventListener('resize', this._onResize);
    }

    disconnectedCallback() {
      removeEventListener('resize', this._onResize);
      document.removeEventListener('keydown', this._onKey);
    }

    _targetRect() {
      const vw = innerWidth, vh = innerHeight;
      const w = Math.min(560, vw - 48);
      const h = Math.min(560, vh - 48, w);
      return { left: (vw - w) / 2, top: (vh - h) / 2, width: w, height: h };
    }

    _sizeCard(r) {
      Object.assign(this.card.style, {
        left: r.left + 'px', top: r.top + 'px',
        width: r.width + 'px', height: r.height + 'px',
      });
    }

    expand() {
      if (this.open) return;
      this.open = true;

      this.backdrop.hidden = false;
      this.card.hidden = false;

      const from = this.badge.getBoundingClientRect();
      const to   = this._targetRect();
      this._sizeCard(to);
      this.card.classList.add('is-flying');
      this.backdrop.classList.add('is-open');

      // FLIP "first": transform the card back onto the badge.
      const sx = from.width / to.width;
      const sy = from.height / to.height;
      const tx = from.left - to.left;
      const ty = from.top - to.top;
      this.card.style.transition = 'none';
      this.card.style.borderRadius = (50 * Math.min(sx, sy)) + '%';
      this.card.style.transform = `translate(${tx}px,${ty}px) scale(${sx},${sy})`;

      requestAnimationFrame(() => requestAnimationFrame(() => {
        this.card.style.transition = REDUCE ? 'none'
          : 'transform .46s cubic-bezier(.22,1,.36,1), border-radius .46s ease';
        this.card.style.transform = 'translate(0,0) scale(1,1)';
        this.card.style.borderRadius = '20px';
        this.card.classList.add('is-shown');
      }));

      this.badge.style.visibility = 'hidden';
      (this.closeBtn || this.card).focus({ preventScroll: true });
      document.addEventListener('keydown', this._onKey);
      document.documentElement.style.overflow = 'hidden';
    }

    close() {
      if (!this.open) return;
      this.open = false;

      const from = this.badge.getBoundingClientRect();
      const to   = this.card.getBoundingClientRect();
      const sx = from.width / to.width;
      const sy = from.height / to.height;
      const tx = from.left - to.left;
      const ty = from.top - to.top;

      this.card.classList.remove('is-shown');
      this.card.style.transition = REDUCE ? 'none'
        : 'transform .4s cubic-bezier(.4,0,.2,1), border-radius .4s ease';
      this.card.style.transform = `translate(${tx}px,${ty}px) scale(${sx},${sy})`;
      this.card.style.borderRadius = (50 * Math.min(sx, sy)) + '%';
      this.backdrop.classList.remove('is-open');

      const done = () => {
        this.card.classList.remove('is-flying');
        this.card.hidden = true;
        this.backdrop.hidden = true;
        this.card.style.cssText = '';      // reset inline geometry + transform
        this.badge.style.visibility = '';
        this.badge.focus({ preventScroll: true });
        this.card.removeEventListener('transitionend', done);
      };
      if (REDUCE) done();
      else this.card.addEventListener('transitionend', done);

      document.removeEventListener('keydown', this._onKey);
      document.documentElement.style.overflow = '';
    }
  }

  if (!customElements.get('lifestyle-blob')) {
    customElements.define('lifestyle-blob', LifestyleBlob);
  }
})();